(() => {
  if (window.CLProductExperience) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const galleries = new Set();
  const modulo = (value, count) => count ? ((value % count) + count) % count : 0;

  const fine = matchMedia('(hover: hover) and (pointer: fine)');
  const normal = value => String(value || '').normalize('NFKC').replace(/&/g, ' and ').replace(/\s+/g, ' ').trim().toLocaleLowerCase();
  const id = value => String(value ?? '');

  function imageLabel(image) {
    const alt = String(image.alt || '');
    const match = alt.match(/^(.*) in (.+),\s*image\s+\d+\b/i)
      || alt.match(/^(.*),\s*product photograph\s+\d+;\s*(.+) option\s*$/i);
    return match ? { product: normal(match[1]), colour: normal(match[2]) } : null;
  }

  function orderMedia(data, variant, images) {
    const anchor = id(variant?.imageId);
    const roles = new Map();
    for (const entry of Array.isArray(data.mediaRoles) ? data.mediaRoles : []) {
      const rank = entry.role === 'worn' ? 1 : entry.role === 'lifestyle' ? 2 : null;
      const key = id(entry.id);
      if (key && rank !== null && (!roles.has(key) || rank < roles.get(key))) roles.set(key, rank);
    }
    const seen = new Set();
    return images.map((image, index) => ({ image, index, key: id(image.id) })).filter(item => {
      if (!item.key) return true;
      if (seen.has(item.key)) return false;
      seen.add(item.key); return true;
    }).sort((a, b) => {
      const rank = item => item.key === anchor ? -1 : roles.get(item.key) || 0;
      return rank(a) - rank(b) || a.index - b.index;
    }).map(item => item.image);
  }

  function resolve(data, variantId, colour) {
    const images = data.images || [];
    const variants = data.variantImages || [];
    const variant = variants.find(v => id(v.id) === id(variantId));
    const result = (selected, reason, source) => ({ images: orderMedia(data, variant, selected), reason, source });
    const anchor = images.find(image => id(image.id) === id(variant?.imageId));
    const wanted = normal(colour);
    const safe = image => {
      const label = imageLabel(image);
      if (wanted && label && label.colour !== wanted) return false;
      const owners = (image.variantIds || []).map(value => variants.find(v => id(v.id) === id(value))).filter(Boolean);
      return owners.every(owner => !wanted || !owner.colour || normal(owner.colour) === wanted);
    };
    const associated = images.filter(image => safe(image) && (image === anchor || (image.variantIds || []).some(value => id(value) === id(variantId))));
    const productNames = new Set([data.title, ...(data.titleAliases || [])].map(normal));
    const verified = images.filter(image => safe(image) && (variant?.verifiedMediaIds || []).some(value => id(value) === id(image.id)));
    if (verified.length) return result(verified, 'visually_verified_product_form', 'verified_display_mapping');
    const expectedFormat = normal((data.formatIndices || []).map(index => variant?.options?.[index] || '').join(' / '));
    const formatted = expectedFormat ? images.filter(image => {
      const label = imageLabel(image);
      const suffix = String(image.alt || '').match(/\s[—–]\s(.+)$/)?.[1];
      return suffix && normal(suffix) === expectedFormat && productNames.has(label?.product) && label.colour === wanted && safe(image);
    }) : [];
    const labelled = wanted && data.title ? images.filter(image => {
      const label = imageLabel(image);
      return productNames.has(label?.product) && label.colour === wanted && safe(image);
    }) : [];
    if (variants.length === 1 && !data.hasMultipleFormats) {
      const single = images.filter(image => {
        const label = imageLabel(image);
        return safe(image) && (!label || productNames.has(label.product));
      });
      return result(single, null, 'single_variant_product');
    }
    if (associated.length) {
      const peers = variants.filter(v => normal(v.colour) === wanted);
      const sharedAnchor = peers.length > 0 && peers.every(v => v.imageId && id(v.imageId) === id(variant?.imageId));
      if (data.hasMultipleFormats && !sharedAnchor) return result([...associated, ...formatted], formatted.length ? null : 'format_requires_explicit_association', 'exact_variant_format_label');
      const selected = new Set([...associated, ...labelled]);
      return result(images.filter(image => selected.has(image)), null, 'shopify_association');
    }
    if (data.hasMultipleFormats) return formatted.length ? result(formatted, null, 'exact_variant_format_label') : result([], 'format_requires_explicit_association', 'placeholder');
    if (labelled.length) return result(labelled, 'verified_colour_fallback', 'exact_product_colour_label');
    return result([], anchor && !safe(anchor) ? 'contradictory_variant_image' : 'missing_variant_image', 'placeholder');
  }

  function sourceSet(image) {
    if (image.srcset) return image.srcset;
    const maximum = Math.min(2000, image.width || 1600);
    return [...new Set([360, 540, 720, 1000, 1400, 1600].filter(w => w < maximum).concat(maximum))].map(width => {
      const url = new URL(image.url, location.origin);
      url.searchParams.set('width', width);
      return `${url.href} ${width}w`;
    }).join(', ');
  }

  class Gallery extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      this.loads = new Map();
      this.animations = [];
      this.token = 0;
      this.navigation = 0;
      this.dragSequence = 0;
      this.dragWaiters = [];
      this.readyToken = null;
      this.index = 0;
      this.targetIndex = 0;
      this.images = [];
      this.touchEvents = 'ontouchstart' in window;
      this.dataset.gestureInput = this.touchEvents ? 'touch' : 'pointer';
      this.dataset.swipe = this.config.swipe === false ? 'off' : 'on';
      this.tabIndex = this.config.swipe === false ? -1 : 0;
      this.setAttribute('role', 'group');
      this.setAttribute('aria-label', this.config.label);
      this.dataset.clPremium = '';
      this.viewport = document.createElement('span');
      this.viewport.className = 'clpg-viewport';
      this.progress = document.createElement('span');
      this.progress.className = 'clpg-progress';
      this.progress.setAttribute('aria-hidden', 'true');
      this.fill = document.createElement('span');
      this.progress.append(this.fill);
      this.status = document.createElement('span');
      this.status.className = 'visually-hidden';
      this.status.setAttribute('aria-live', 'polite');
      this.empty = document.createElement('span');
      this.empty.className = 'clpg-empty';
      this.empty.setAttribute('role', 'img');
      this.empty.setAttribute('aria-label', 'Image unavailable');
      this.empty.textContent = 'Image unavailable';
      this.append(this.viewport);
      if (this.config.swipe !== false) this.append(this.progress, this.status);
      const options = { signal: this.events.signal };
      this.addEventListener('pointerdown', e => {
        if (this.touchEvents && e.pointerType === 'touch') {
          if (this.config.swipe !== false && this.images.length > 1) e.stopPropagation();
          return;
        }
        this.down(e);
      }, options);
      this.addEventListener('mousedown', e => {
        if (this.config.swipe !== false && this.images.length > 1) e.stopPropagation();
      }, options);
      this.addEventListener('pointermove', e => {
        if (this.drag?.input !== 'touch') this.move(e);
      }, options);
      this.addEventListener('pointerup', e => {
        if (this.drag?.input !== 'touch') this.up(e);
      }, options);
      this.addEventListener('pointercancel', e => {
        if (this.drag?.input !== 'touch' && this.drag?.pointer === e.pointerId) this.cancelDrag();
      }, options);
      this.addEventListener('lostpointercapture', e => {
        if (e.target === this && this.drag?.input !== 'touch' && this.drag?.pointer === e.pointerId) this.cancelDrag();
      }, options);
      if (this.touchEvents) {
        const touchOptions = { ...options, capture: true, passive: false };
        this.addEventListener('touchstart', e => {
          if (e.touches.length !== 1) { this.cancelDrag(); return; }
          this.down(this.touchEvent(e, e.touches[0]));
        }, touchOptions);
        this.addEventListener('touchmove', e => {
          if (this.drag?.input !== 'touch') return;
          if (e.touches.length !== 1) { this.cancelDrag(); return; }
          const touch = Array.from(e.touches).find(point => point.identifier === this.drag.pointer);
          if (touch) this.move(this.touchEvent(e, touch));
        }, touchOptions);
        this.addEventListener('touchend', e => {
          if (this.drag?.input !== 'touch') return;
          const touch = Array.from(e.changedTouches).find(point => point.identifier === this.drag.pointer);
          if (touch) this.up(this.touchEvent(e, touch));
        }, touchOptions);
        this.addEventListener('touchcancel', e => {
          if (this.drag?.input === 'touch' && Array.from(e.changedTouches).some(point => point.identifier === this.drag.pointer)) this.cancelDrag();
        }, touchOptions);
      }
      this.addEventListener('dragstart', e => e.preventDefault(), options);
      this.addEventListener('click', e => {
        if (performance.now() < (this.suppressUntil || 0)) {
          e.preventDefault(); e.stopImmediatePropagation();
        }
      }, { ...options, capture: true });
      this.addEventListener('keydown', e => {
        if (this.config.swipe === false) return;
        const next = e.key === 'ArrowRight' ? this.targetIndex + 1 : e.key === 'ArrowLeft' ? this.targetIndex - 1 : e.key === 'Home' ? 0 : e.key === 'End' ? this.images.length - 1 : null;
        if (next === null || this.images.length < 2) return;
        e.preventDefault(); e.stopPropagation(); this.show(next);
      }, options);
      this.addEventListener('pointerenter', e => {
        if (this.config.card && fine.matches && e.pointerType === 'mouse') this.hover(true);
      }, options);
      this.addEventListener('pointerleave', () => this.hover(false), options);
      reduced.addEventListener('change', () => this.finishAnimations(), options);
      this.resize = new ResizeObserver(() => {
        if (this.drag && Math.abs(this.clientWidth - this.drag.width) > .5) this.cancelDrag();
      });
      this.resize.observe(this);
    }

    disconnectedCallback() {
      this.token++;
      this.navigation++;
      this.selectionDone?.(); this.selectionDone = null;
      this.cancelDrag();
      this.events?.abort(); this.events = null;
      this.resize?.disconnect();
      cancelAnimationFrame(this.frame);
      this.finishAnimations();
      this.loads?.clear();
      this.replaceChildren();
      this.config.onDisconnect?.();
    }

    finishAnimations() {
      this.animations?.forEach(animation => animation.cancel());
      this.animations = [];
      this.outgoing?.remove(); this.outgoing = null;
      this.viewport?.querySelectorAll('.clpg-slide').forEach(slide => { slide.style.transform = ''; });
      this.removeAttribute('data-clpg-hover');
    }

    async image(info) {
      const key = id(info.id);
      if (this.loads.has(key)) return this.loads.get(key);
      const promise = (async () => {
        const image = new Image();
        image.alt = info.alt || this.config.label;
        image.width = info.width || 1600; image.height = info.height || 1600;
        image.decoding = 'async'; image.draggable = false;
        image.dataset.clGalleryImage = ''; image.dataset.clFrontSelected = 'true';
        image.sizes = this.config.sizes;
        const loaded = new Promise((accept, reject) => {
          const signal = this.events.signal;
          const cleanup = () => { clearTimeout(timeout); image.onload = null; image.onerror = null; signal.removeEventListener('abort', abort); };
          const abort = () => { cleanup(); image.removeAttribute('srcset'); image.removeAttribute('src'); reject(new Error('Gallery disconnected')); };
          const timeout = setTimeout(() => { cleanup(); reject(new Error('Image timeout')); }, 15000);
          image.onload = () => { cleanup(); accept(); };
          image.onerror = () => { cleanup(); reject(new Error('Image unavailable')); };
          signal.addEventListener('abort', abort, { once: true });
        });
        image.srcset = window.CLStudio?.srcset(sourceSet(info)) || sourceSet(info);
        image.src = window.CLStudio?.url(info.url) || info.url;
        await loaded;
        await image.decode().catch(() => {});
        if (this.config.card && this.isConnected && window.CLCardPresentation) await window.CLCardPresentation.prepare(image, this.config.frame, this.config.card, info.alt);
        return image;
      })();
      this.loads.set(key, promise);
      promise.catch(() => this.loads.delete(key));
      return promise;
    }

    makeSlide(image, position) {
      const slide = document.createElement('span');
      slide.className = 'clpg-slide';
      slide.style.setProperty('--clpg-position', position);
      slide.append(image.cloneNode(true));
      return slide;
    }

    async select(images, fade = true, startIndex = 0) {
      ++this.token; ++this.navigation;
      this.selectionDone?.(); this.selectionDone = null;
      this.cancelDrag(); this.finishAnimations();
      // Accept the first gesture while its already-known photographs are decoding.
      this.images = images; this.index = startIndex; this.targetIndex = startIndex;
      this.dataset.count = images.length;
      this.pending = true;
      this.setAttribute('aria-busy', 'true');
      this.progress.hidden = true;
      if (!images.length) {
        this.viewport.replaceChildren(this.empty);
        this.pending = false; this.removeAttribute('aria-busy'); this.dataset.count = '0';
        this.config.frame?.classList.add('clpg-ready');
        this.mark(); return;
      }
      const ready = new Promise(resolve => { this.selectionDone = resolve; });
      this.show(startIndex, fade, true);
      if (images[startIndex + 1] && (this.config.swipe !== false || fine.matches)) this.image(images[startIndex + 1]).catch(() => {});
      return ready;
    }

    mark() {
      this.dataset.index = this.index;
      const interactive = this.config.swipe !== false;
      this.progress.hidden = !interactive || this.images.length < 2;
      this.fill.style.transform = `scaleX(${this.images.length ? (this.index + 1) / this.images.length : 0})`;
      const text = this.images.length ? `${this.index + 1} / ${this.images.length}` : 'Image unavailable';
      this.status.textContent = interactive ? text : '';
      this.setAttribute('aria-label', interactive ? `${this.config.label} · ${text}` : this.config.label);
      this.config.onChange?.(this.index, this.images);
    }

    adjacent() {
      const token = this.token;
      const index = this.index;
      const wanted = (this.config.swipe === false ? fine.matches ? [1] : [] : [index - 1, index + 1]).filter(i => i >= 0 && i < this.images.length);
      this.viewport.querySelectorAll('[data-adjacent]').forEach(slide => slide.remove());
      for (const i of wanted) this.image(this.images[i]).then(image => {
        if (this.token !== token || this.index !== index || !this.isConnected) return;
        const slide = this.makeSlide(image, i - index);
        slide.dataset.adjacent = i;
        this.viewport.append(slide);
      }).catch(() => {});
      const keep = new Set([index, ...wanted].map(i => id(this.images[i]?.id)));
      for (const key of this.loads.keys()) if (!keep.has(key)) this.loads.delete(key);
    }

    async show(value, animate = true, selecting = false) {
      if (this.config.swipe === false && !selecting) return;
      if (!this.images.length) return;
      const next = Math.max(0, Math.min(this.images.length - 1, value));
      const token = this.token;
      // Loads may complete out of order; only the latest intention can commit.
      const navigation = ++this.navigation;
      const sequence = this.dragSequence;
      const offset = this.dragOffset || 0;
      this.targetIndex = next;
      this.finishAnimations(); this.hover(false);
      if (next === this.index && this.readyToken === token) { this.settle(offset, animate); return; }
      try {
        const image = await this.image(this.images[next]);
        if (!await this.waitForDrag(token, navigation)) return;
        const old = this.viewport.querySelector('[data-current]');
        const initial = this.readyToken !== token;
        const direction = Math.sign(next - this.index);
        const dragOffset = sequence === this.dragSequence ? offset : 0;
        const incoming = this.makeSlide(image, 0); incoming.dataset.current = '';
        this.viewport.replaceChildren(incoming);
        this.index = next; this.dragOffset = 0;
        this.readyToken = token;
        this.pending = false; this.removeAttribute('aria-busy');
        this.dataset.count = this.images.length;
        this.config.frame?.classList.add('clpg-ready');
        if (animate && !reduced.matches && old) {
          this.outgoing = old; old.removeAttribute('data-current');
          let a, b;
          if (initial) {
            this.append(old); old.classList.add('clpg-outgoing');
            a = old.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 240, easing: 'ease-in-out' });
            b = incoming.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 240, easing: 'ease-in-out' });
          } else {
            this.viewport.append(old); old.style.setProperty('--clpg-position', -direction);
            const width = this.clientWidth;
            a = old.animate([{ transform: `translateX(${dragOffset}px)` }, { transform: `translateX(${-direction * width}px)` }], { duration: 240, easing: 'cubic-bezier(.22,.61,.36,1)' });
            b = incoming.animate([{ transform: `translateX(${direction * width + dragOffset}px)` }, { transform: 'translateX(0)' }], { duration: 240, easing: 'cubic-bezier(.22,.61,.36,1)' });
          }
          this.animations = [a, b];
          a.finished.then(() => { old.remove(); if (this.outgoing === old) this.outgoing = null; }).catch(() => {});
        }
        this.mark(); this.adjacent();
        this.selectionDone?.(); this.selectionDone = null;
      } catch {
        if (!await this.waitForDrag(token, navigation)) return;
        this.targetIndex = this.index;
        if (this.readyToken === token) { this.settle(sequence === this.dragSequence ? offset : 0, animate); return; }
        this.pending = false; this.removeAttribute('aria-busy');
        this.images = []; this.index = 0; this.targetIndex = 0;
        this.viewport.replaceChildren(this.empty); this.dataset.count = '0';
        this.config.frame?.classList.add('clpg-ready');
        this.mark(); this.config.onError?.();
        this.selectionDone?.(); this.selectionDone = null;
      }
    }

    settle(offset, animate = true) {
      this.dragOffset = 0;
      const slide = this.viewport.querySelector('[data-current]');
      if (slide && animate && !reduced.matches && offset) this.animations = [slide.animate([{ transform: `translateX(${offset}px)` }, { transform: 'translateX(0)' }], { duration: 220, easing: 'ease-in-out' })];
      this.viewport.style.transform = '';
    }

    touchEvent(event, touch) {
      return {
        input: 'touch', pointerId: touch.identifier, isPrimary: true, button: 0,
        clientX: touch.clientX, clientY: touch.clientY, timeStamp: event.timeStamp,
        cancelable: event.cancelable,
        preventDefault: () => event.preventDefault(),
        stopPropagation: () => event.stopPropagation()
      };
    }

    releaseDrag() {
      this.drag = null;
      const waiting = this.dragWaiters.splice(0);
      waiting.forEach(resolve => resolve());
    }

    async waitForDrag(token, navigation) {
      while (this.drag && token === this.token && navigation === this.navigation && this.isConnected) {
        await new Promise(resolve => this.dragWaiters.push(resolve));
      }
      return token === this.token && navigation === this.navigation && this.isConnected;
    }

    down(e) {
      if (this.config.swipe === false) return;
      if (this.images.length < 2 || !e.isPrimary || e.button !== 0) return;
      this.finishAnimations(); this.hover(false);
      this.dragSequence++; this.dragOffset = 0;
      e.stopPropagation();
      this.drag = { input: e.input || 'pointer', pointer: e.pointerId, x: e.clientX, y: e.clientY, lastX: e.clientX, time: e.timeStamp, velocity: 0, width: this.clientWidth, locked: false };
    }

    move(e) {
      const drag = this.drag;
      if (!drag || drag.pointer !== e.pointerId) return;
      const dx = e.clientX - drag.x, dy = e.clientY - drag.y;
      if (drag.input === 'touch' && e.cancelable === false) { this.cancelDrag(); return; }
      if (!drag.locked) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) < 8) return;
        if (Math.abs(dy) >= Math.abs(dx)) { this.releaseDrag(); return; }
        drag.locked = true;
        if (drag.input !== 'touch') {
          try { this.setPointerCapture(e.pointerId); } catch { this.cancelDrag(); return; }
        }
      }
      if (e.cancelable !== false) e.preventDefault();
      e.stopPropagation();
      const elapsed = Math.max(1, e.timeStamp - drag.time);
      drag.velocity = (e.clientX - drag.lastX) / elapsed;
      drag.lastX = e.clientX; drag.time = e.timeStamp;
      this.dragOffset = (this.targetIndex === 0 && dx > 0) || (this.targetIndex === this.images.length - 1 && dx < 0) ? dx * .22 : dx;
      if (!this.frame) this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        if (this.drag?.locked) this.viewport.style.transform = `translate3d(${this.dragOffset}px,0,0)`;
      });
    }

    up(e) {
      const drag = this.drag;
      if (!drag || e.pointerId !== drag.pointer) return;
      this.releaseDrag();
      if (!drag.locked) return;
      if (e.cancelable !== false) e.preventDefault();
      e.stopPropagation();
      cancelAnimationFrame(this.frame); this.frame = 0;
      this.suppressUntil = performance.now() + 450;
      const dx = e.clientX - drag.x;
      const velocity = e.timeStamp - drag.time < 100 ? drag.velocity : 0;
      const distance = Math.abs(dx);
      // A tiny release correction must not reverse the direction already dragged.
      const projected = distance + Math.max(0, velocity * Math.sign(dx)) * 100;
      const threshold = Math.min(80, drag.width * .2);
      const step = distance >= threshold || (distance >= 12 && projected > threshold) ? -Math.sign(dx) : 0;
      this.viewport.style.transform = '';
      this.show(this.targetIndex + step);
      if (drag.input !== 'touch' && this.hasPointerCapture(e.pointerId)) this.releasePointerCapture(e.pointerId);
    }

    cancelDrag() {
      if (!this.drag) return;
      const drag = this.drag; this.releaseDrag();
      if (drag.locked) this.suppressUntil = performance.now() + 450;
      cancelAnimationFrame(this.frame); this.frame = 0;
      if (drag.input !== 'touch' && this.hasPointerCapture(drag.pointer)) this.releasePointerCapture(drag.pointer);
      const offset = this.dragOffset || 0;
      this.viewport.style.transform = ''; this.settle(offset);
    }

    async hover(active) {
      this.hovered = active;
      if (!active) { this.removeAttribute('data-clpg-hover'); return; }
      if (this.pending || this.drag || this.images.length < 2 || !fine.matches) return;
      const token = this.token;
      try {
        const image = await this.image(this.images[1]);
        if (token !== this.token || !this.hovered || !this.isConnected || this.drag) return;
        this.hoverLayer?.remove();
        this.hoverLayer = this.makeSlide(image, 0); this.hoverLayer.classList.add('clpg-hover');
        this.append(this.hoverLayer); this.setAttribute('data-clpg-hover', '');
      } catch {}
    }
  }

  customElements.define('cl-product-media-player', Gallery);

  function create(mount, config) {
    const element = document.createElement('cl-product-media-player');
    element.config = config; mount.append(element); return element;
  }


  function buildRibbon(data, variantId, options) {
    const variants = data.variantImages || [];
    const active = variants.find(variant => id(variant.id) === id(variantId));
    if (!active) return [];
    const entries = new Map();
    for (const variant of variants) {
      for (const image of resolve(data, variant.id, variant.colour).images) {
        const key = id(image.id) || image.url;
        const entry = entries.get(key);
        if (entry) entry.owners.push(variant);
        else entries.set(key, { image, owners: [variant] });
      }
    }
    return [...entries.values()].map(({ image, owners }) => {
      const owner = owners.find(variant => id(variant.id) === id(active.id))
        || owners.find(variant => (data.formatIndices || []).every(index => variant.options?.[index] === active.options?.[index]))
        || owners[0];
      return { ...image, colour: owner.colour || '', targetVariantId: owner.id };
    });
  }

  function createGallery(shell, label, data, callbacks = {}) {
    const root = shell.querySelector('[data-cl-block-gallery]');
    const events = new AbortController(), signal = events.signal;
    const rail = document.createElement('div');
    rail.className = 'clp-thumbnail-rail';
    rail.setAttribute('role', 'group');
    rail.setAttribute('aria-label', root.getAttribute('aria-label'));
    const controls = document.createElement('div');
    controls.className = 'clp-gallery-controls';
    const counter = document.createElement('span');
    counter.className = 'clp-gallery-counter';
    counter.setAttribute('aria-hidden', 'true');
    const arrow = (direction, text) => {
      const button = document.createElement('button');
      button.type = 'button'; button.setAttribute('aria-label', text);
      button.innerHTML = `<svg viewBox="0 0 20 20" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.2" aria-hidden="true"><path d="${direction < 0 ? 'm12 4-6 6 6 6' : 'm8 4 6 6-6 6'}"/></svg>`;
      button.addEventListener('click', () => gallery.show(gallery.index + direction), { signal });
      return button;
    };
    const previous = arrow(-1, shell.dataset.previousLabel);
    const next = arrow(1, shell.dataset.nextLabel);
    controls.append(previous, counter, next);
    shell.append(rail, controls);
    root.replaceChildren();
    root.dataset.clpgFrame = "";
    let selectedOptions, destroyed = false;
    const state = { images: [], shell, select, destroy, get activeImage() { return state.images[gallery.index]; } };
    const gallery = create(root, {
      label: root.getAttribute('aria-label') || label,
      frame: root,
      sizes: '(min-width: 750px) 60vw, 100vw',
      onChange(index, images) {
        const info = images[index];
        gallery.progress.hidden = true;
        root.dataset.ready = 'true'; root.dataset.index = String(index); root.dataset.count = String(images.length);
        shell.dataset.clActiveMedia = info?.id || '';
        counter.textContent = images.length ? `${index + 1} / ${images.length}` : '0 / 0';
        previous.disabled = !images.length || index === 0;
        next.disabled = !images.length || index === images.length - 1;
        rail.querySelectorAll('button').forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
        const active = rail.children[index];
        if (active) {
          const left = active.offsetLeft, right = left + active.offsetWidth;
          if (left < rail.scrollLeft || right > rail.scrollLeft + rail.clientWidth) rail.scrollTo({ left: Math.max(0, left - (rail.clientWidth - active.offsetWidth) / 2), behavior: reduced.matches ? 'instant' : 'smooth' });
        }
        const options = selectedOptions; selectedOptions = undefined;
        callbacks.onSelect?.(info, options);
        callbacks.onBusy?.(false);
      },
      onDisconnect() { events.abort(); galleries.delete(state); }
    });
    function measureThumbnails() {
      const width = Math.max(0, rail.clientWidth - 2);
      const target = matchMedia('(min-width: 750px)').matches ? 72 : 64;
      const visible = Math.max(1, Math.floor((width + 8) / (target + 8)));
      const size = state.images.length > visible ? (width - 8 * (visible - 1)) / visible : target;
      rail.style.setProperty('--clp-thumbnail-size', `${Math.max(44, size)}px`);
    }
    const thumbnailResize = new ResizeObserver(measureThumbnails);
    thumbnailResize.observe(rail);
    function select(images, fade = true, variantId, options) {
      state.images = buildRibbon(data, variantId, options);
      selectedOptions = options;
      root.dataset.ready = 'false';
      callbacks.onBusy?.(true, variantId);
      rail.replaceChildren(); rail.scrollLeft = 0;
      for (const [index, info] of state.images.entries()) {
        const button = document.createElement('button'); button.type = 'button';
        button.setAttribute('aria-label', `${shell.dataset.photoLabel} ${index + 1} / ${state.images.length} · ${info.alt || label}`);
        button.setAttribute('aria-pressed', String(index === 0));
        const photo = new Image(); photo.alt = ''; photo.width = 72; photo.height = 72;
        photo.loading = 'lazy'; photo.decoding = 'async'; photo.draggable = false;
        const url = new URL(window.CLStudio?.url(info.url) || info.url, location.origin);
        url.searchParams.set('width', '144'); photo.src = url.href;
        button.append(photo);
        button.addEventListener('click', () => gallery.show(index), { signal });
        rail.append(button);
      }
      rail.hidden = !state.images.length; measureThumbnails();
      const anchor = images[0]?.id;
      const startIndex = Math.max(0, state.images.findIndex(image => id(image.id) === id(anchor)));
      return gallery.select(state.images, fade, startIndex);
    }
    rail.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      gallery.show(event.key === 'Home' ? 0 : event.key === 'End' ? state.images.length - 1 : gallery.index + (event.key === 'ArrowRight' ? 1 : -1));
    }, { signal });
    function destroy() {
      if (destroyed) return;
      destroyed = true; events.abort(); thumbnailResize.disconnect(); gallery.remove(); controls.remove(); rail.remove(); galleries.delete(state);
    }
    galleries.add(state);
    return state;
  }


  document.addEventListener('shopify:section:unload', e => { for (const gallery of galleries) if (e.target.contains(gallery.shell)) gallery.destroy(); });
  function initReveal(root = document) {
    root.querySelectorAll('[data-cl-reveal]').forEach(section => {
      if (section.dataset.revealReady) return;
      section.dataset.revealReady = 'true';
      const scene = document.querySelector('[data-clo-product]')?.closest('.shopify-section');
      if (scene && !scene.dataset.clStickyScene) {
        scene.dataset.clStickyScene = 'true';
        const cleanup = () => {
          resize.disconnect(); document.removeEventListener('shopify:section:unload', unload);
        };
        const unload = event => { if (event.target.contains(scene)) cleanup(); };
        const measure = () => {
          if (!scene.isConnected) { cleanup(); return; }
          const viewport = document.documentElement.clientHeight;
          const header = document.querySelector('header-component')?.offsetHeight || 0;
          const threshold = header + .4 * Math.max(0, viewport - header);
          scene.style.setProperty('--clp-scene-top', `${Math.min(0, threshold - scene.offsetHeight)}px`);
          scene.dataset.clImmersiveThreshold = String(threshold);
          scene.dataset.clImmersiveRatio = '0.4';
        };
        const resize = new ResizeObserver(measure);
        resize.observe(scene);
        resize.observe(document.documentElement);
        const header = document.querySelector('header-component');
        if (header) resize.observe(header);
        document.addEventListener('shopify:section:unload', unload);
        measure();
        scene.classList.add('clp-sticky-scene');
      }
      if (section.matches('[data-cl-recommendations]') && !section.dataset.loaded) {
        section.dataset.loaded = 'true';
        let scrollRoot = section.parentElement;
        while (scrollRoot && !(/auto|scroll/.test(getComputedStyle(scrollRoot).overflowY) && scrollRoot.scrollHeight > scrollRoot.clientHeight)) scrollRoot = scrollRoot.parentElement;
        if (scrollRoot === document.body || scrollRoot === document.documentElement) scrollRoot = null;
        const request = new AbortController();
        const unload = event => {
          if (!event.target.contains(section)) return;
          load.disconnect(); request.abort(); document.removeEventListener('shopify:section:unload', unload);
        };
        document.addEventListener('shopify:section:unload', unload);
        const load = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          load.disconnect();
          section.setAttribute('aria-busy', 'true');
          fetch(section.dataset.url, { headers: { Accept: 'text/html' }, signal: request.signal })
            .then(response => { if (!response.ok) throw new Error('Recommendations unavailable'); return response.text(); })
            .then(html => {
              if (!section.isConnected) return;
              const parsed = new DOMParser().parseFromString(html, 'text/html');
              const grid = parsed.querySelector('[data-cl-recommendations-grid]');
              if (grid?.children.length) section.querySelector('[data-cl-recommendations-grid]').replaceChildren(...grid.children);
            }).catch(() => {}).finally(() => {
              if (section.isConnected) section.removeAttribute('aria-busy');
              document.removeEventListener('shopify:section:unload', unload);
            });
        }, { root: scrollRoot, rootMargin: '700px' });
        load.observe(section);
      }
    });
  }
  window.CLProductExperience = { createGallery, buildRibbon, modulo, resolve };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initReveal(), { once: true }); else initReveal();
  document.addEventListener('shopify:section:load', e => initReveal(e.target));
})();

